import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useFocusedPatient } from '@/hooks/useFocusedPatient';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Calendar,
  AlertTriangle,
  Clock,
  User,
  Package,
  Check,
  ChevronRight
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Tables } from '@/integrations/supabase/types';

type Patient = Tables<'patients'>;

interface PatientWithRole extends Patient {
  role: string;
}

interface PatientStats {
  shiftsLast30Days: number;
  incidentsLast30Days: number;
  activeShift: {
    isActive: boolean;
    caregiverName: string | null;
  };
  inventoryAlerts: number;
  caregiverStats?: {
    shiftsCount: number;
    hoursWorked: number;
  };
}

interface PatientStatsCardProps {
  patient: PatientWithRole;
}

export function PatientStatsCard({ patient }: PatientStatsCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { focusedPatient, setFocusedPatientId } = useFocusedPatient();
  const [stats, setStats] = useState<PatientStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCompanyPatient, setIsCompanyPatient] = useState(false);

  const isFocused = focusedPatient?.id === patient.id;
  const isCaregiver = patient.role === 'caregiver';

  useEffect(() => {
    fetchStats();
    checkIfCompanyPatient();
  }, [patient.id, user?.id]);

  const checkIfCompanyPatient = async () => {
    const { data } = await supabase
      .from('company_patient_assignments')
      .select('id')
      .eq('patient_id', patient.id)
      .eq('is_active', true)
      .limit(1);
    setIsCompanyPatient((data?.length || 0) > 0);
  };

  const fetchStats = async () => {
    if (!user) return;

    try {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const thirtyDaysAgoStr = thirtyDaysAgo.toISOString();

      const { data: shifts, error: shiftsError } = await supabase
        .from('shifts')
        .select('id, caregiver_id, check_in_at, check_out_at, status')
        .eq('patient_id', patient.id)
        .gte('check_in_at', thirtyDaysAgoStr);

      if (shiftsError) throw shiftsError;

      // Count incidents: care_plan_tasks with task_type='incident' for this patient
      const { data: incidentTaskIds } = await supabase
        .from('care_plan_tasks')
        .select('id')
        .eq('patient_id', patient.id)
        .eq('task_type', 'incident');

      const taskIds = (incidentTaskIds || []).map(t => t.id);

      let totalIncidents = 0;

      // Count adhoc incidents
      const { count: adhocCount } = await supabase
        .from('task_executions')
        .select('*', { count: 'exact', head: true })
        .eq('patient_id', patient.id)
        .gte('executed_at', thirtyDaysAgoStr)
        .eq('adhoc_task_type', 'incident');

      totalIncidents += adhocCount || 0;

      // Count care plan incident executions
      if (taskIds.length > 0) {
        const { count: taskIncidentCount } = await supabase
          .from('task_executions')
          .select('*', { count: 'exact', head: true })
          .eq('patient_id', patient.id)
          .gte('executed_at', thirtyDaysAgoStr)
          .in('task_id', taskIds);

        totalIncidents += taskIncidentCount || 0;
      }

      const { data: activeShifts, error: activeError } = await supabase
        .from('shifts')
        .select('id, caregiver_id')
        .eq('patient_id', patient.id)
        .eq('status', 'active')
        .limit(1);

      if (activeError) throw activeError;

      let caregiverName: string | null = null;
      if (activeShifts && activeShifts.length > 0) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('user_id', activeShifts[0].caregiver_id)
          .maybeSingle();
        caregiverName = profile?.full_name || t('roles.caregiver');
      }

      const { data: inventoryAlerts, error: inventoryError } = await supabase
        .from('inventory')
        .select('id')
        .eq('patient_id', patient.id)
        .neq('status', 'ok');

      if (inventoryError) throw inventoryError;

      let caregiverStats: PatientStats['caregiverStats'] | undefined;
      if (isCaregiver) {
        const caregiverShifts = (shifts || []).filter(s => s.caregiver_id === user.id);
        let totalHours = 0;

        caregiverShifts.forEach(shift => {
          const checkIn = new Date(shift.check_in_at);
          const checkOut = shift.check_out_at ? new Date(shift.check_out_at) : new Date();
          const hours = (checkOut.getTime() - checkIn.getTime()) / (1000 * 60 * 60);
          totalHours += hours;
        });

        caregiverStats = {
          shiftsCount: caregiverShifts.length,
          hoursWorked: Math.round(totalHours)
        };
      }

      setStats({
        shiftsLast30Days: shifts?.length || 0,
        incidentsLast30Days: totalIncidents,
        activeShift: {
          isActive: (activeShifts?.length || 0) > 0,
          caregiverName
        },
        inventoryAlerts: inventoryAlerts?.length || 0,
        caregiverStats
      });
    } catch (error) {
      console.error('Error fetching patient stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    setFocusedPatientId(patient.id);
    navigate(`/app/patients/${patient.id}`);
  };

  return (
    <Card
      className={cn(
        "cursor-pointer transition-all hover:shadow-md",
        isFocused && "ring-2 ring-success"
      )}
      onClick={handleClick}
    >
      <CardContent className="p-4">
        {/* Header with avatar and name */}
        <div className="flex items-start gap-4 mb-4">
          <div className="relative">
            <SignedAvatar
              src={patient.photo_url}
              bucket="patient-photos"
              className="w-16 h-16"
              fallback={
                <span className="bg-primary/10 text-primary text-xl flex items-center justify-center w-full h-full">
                  {patient.nickname.charAt(0).toUpperCase()}
                </span>
              }
            />
            {isFocused && (
              <div className="absolute -bottom-1 -right-1 bg-primary text-primary-foreground rounded-full p-1">
                <Check className="w-3 h-3" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-lg truncate">{patient.nickname}</h3>
            </div>
            <p className="text-sm text-muted-foreground">
              {patient.role === 'owner'
                ? (isCompanyPatient ? t('roles.manager') : t('roles.owner'))
                : patient.role === 'caregiver' ? t('roles.caregiver') : t('roles.observer')}
            </p>

            {/* Active shift indicator */}
            {isLoading ? (
              <Skeleton className="h-5 w-32 mt-1" />
            ) : stats?.activeShift.isActive ? (
              <div className="flex items-center gap-1 mt-1">
                <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
                <span className="text-xs text-success font-medium">
                  {t('shift.activeShift')}: {stats.activeShift.caregiverName}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-1 mt-1">
                <div className="w-2 h-2 rounded-full bg-muted-foreground/40" />
                <span className="text-xs text-muted-foreground">{t('shift.noActiveShift')}</span>
              </div>
            )}
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground mt-2" />
        </div>

        {/* Stats Grid */}
        {isLoading ? (
          <div className="grid grid-cols-3 gap-3">
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className="h-16 rounded-lg" />
            ))}
          </div>
        ) : stats && (
          <>
            <div className="grid grid-cols-3 gap-3">
              {/* Shifts last 30 days */}
              <div className="bg-accent/50 rounded-lg p-3 text-center">
                <Calendar className="w-5 h-5 mx-auto mb-1 text-primary" />
                <p className="text-2xl font-bold">{stats.shiftsLast30Days}</p>
                <p className="text-xs text-muted-foreground">{t('shift.shifts30d')}</p>
              </div>

              {/* Incidents last 30 days */}
              <div className={cn(
                "rounded-lg p-3 text-center",
                stats.incidentsLast30Days > 0
                  ? "bg-destructive/10"
                  : "bg-accent/50"
              )}>
                <AlertTriangle className={cn(
                  "w-5 h-5 mx-auto mb-1",
                  stats.incidentsLast30Days > 0 ? "text-destructive" : "text-muted-foreground"
                )} />
                <p className={cn(
                  "text-2xl font-bold",
                  stats.incidentsLast30Days > 0 && "text-destructive"
                )}>
                  {stats.incidentsLast30Days}
                </p>
                <p className="text-xs text-muted-foreground">{t('incidents.incidents30d')}</p>
              </div>

              {/* Inventory alerts */}
              <div className={cn(
                "rounded-lg p-3 text-center",
                stats.inventoryAlerts > 0
                  ? "bg-warning/10"
                  : "bg-accent/50"
              )}>
                <Package className={cn(
                  "w-5 h-5 mx-auto mb-1",
                  stats.inventoryAlerts > 0 ? "text-warning" : "text-muted-foreground"
                )} />
                <p className={cn(
                  "text-2xl font-bold",
                  stats.inventoryAlerts > 0 && "text-warning"
                )}>
                  {stats.inventoryAlerts}
                </p>
                <p className="text-xs text-muted-foreground">{t('inventory.stockAlerts')}</p>
              </div>
            </div>

            {/* Caregiver specific stats */}
            {isCaregiver && stats.caregiverStats && (
              <div className="mt-3 pt-3 border-t">
                <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {t('shift.yourCaregiverData')}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 text-center">
                    <Calendar className="w-4 h-4 mx-auto mb-1 text-primary" />
                    <p className="text-xl font-bold text-primary">{stats.caregiverStats.shiftsCount}</p>
                    <p className="text-xs text-muted-foreground">{t('shift.shifts30d')}</p>
                  </div>
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 text-center">
                    <Clock className="w-4 h-4 mx-auto mb-1 text-primary" />
                    <p className="text-xl font-bold text-primary">{stats.caregiverStats.hoursWorked}h</p>
                    <p className="text-xs text-muted-foreground">{t('shift.hoursWorked')}</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}